import React, { useState } from 'react';
import { Activity, ArrowLeft, ArrowRight, Calendar, ChevronDown, ChevronUp, Clock, Download, FileText, Target, TrendingUp } from 'lucide-react';
import RecoveryProgress from './recovery/RecoveryProgress';
import BaselineComparison from './recovery/BaselineComparison';
import ProgressReport from './recovery/ProgressReport';

export interface BaselineMetrics {
  symmetryIndex: number;
  velocity: number;
  strideLength: number;
  stepTime: number;
  cadence: number;
  balanceScore: number;
  weightBearing: number;
  rangeOfMotion: number;
}

export interface RecommendedExercise {
  id: string;
  name: string;
  description: string;
  frequency: string;
  duration: string;
  videoUrl?: string;
  completed: boolean;
}

export interface RecoveryPhase {
  id: string;
  name: string;
  description: string;
  targetMetrics: {
    [metric: string]: number;
  };
  exercises: RecommendedExercise[];
  completed: boolean;
  startDate?: Date;
  completionDate?: Date;
}

export interface ProgressSnapshot {
  id: string;
  date: Date;
  metrics: {
    [metric: string]: number;
  };
  patientReported: {
    painLevel: number;
    confidence: number;
    difficulty: number;
  };
  notes: string;
  phase: number;
}

export interface RecoveryPlan {
  id: string;
  patientId: string;
  patientName: string;
  injuryType: string;
  injuryDate: Date;
  recoveryStart: Date;
  estimatedRecoveryDuration: number; // in days
  phases: RecoveryPhase[];
  baselineMetrics?: BaselineMetrics;
  currentPhase: number;
  lastAssessment: Date;
  progressSnapshots: ProgressSnapshot[];
}

// Generate mock data for testing
const generateMockRecoveryPlan = (): RecoveryPlan => {
  const today = new Date();
  const injuryDate = new Date(today);
  injuryDate.setDate(today.getDate() - 90); // Injury was 90 days ago
  
  const recoveryStart = new Date(injuryDate);
  recoveryStart.setDate(injuryDate.getDate() + 7); // Recovery started 7 days after injury
  
  // Generate baseline metrics (pre-injury state)
  const baselineMetrics: BaselineMetrics = {
    symmetryIndex: 92,
    velocity: 110,
    strideLength: 142,
    stepTime: 0.55,
    cadence: 105,
    balanceScore: 88,
    weightBearing: 95,
    rangeOfMotion: 90
  };
  
  // Create recovery phases
  const phases: RecoveryPhase[] = [
    {
      id: 'phase-1',
      name: 'Initial Recovery',
      description: 'Focus on pain management and basic movement restoration',
      targetMetrics: {
        symmetryIndex: 60,
        velocity: 50,
        strideLength: 90,
        stepTime: 0.9,
        cadence: 70,
        balanceScore: 45,
        weightBearing: 60,
        rangeOfMotion: 50
      },
      exercises: [
        {
          id: 'ex-1-1',
          name: 'Assisted Weight Shifting',
          description: 'Gentle weight shifting exercises with support',
          frequency: 'Daily',
          duration: '5-10 minutes',
          completed: true
        },
        {
          id: 'ex-1-2',
          name: 'Seated Leg Lifts',
          description: 'Seated exercises to improve leg strength',
          frequency: 'Twice daily',
          duration: '5 minutes',
          completed: true
        }
      ],
      completed: true,
      startDate: new Date(recoveryStart),
      completionDate: new Date(recoveryStart.getTime() + 30 * 24 * 60 * 60 * 1000) // 30 days after recovery start
    },
    {
      id: 'phase-2',
      name: 'Intermediate Recovery',
      description: 'Progress to independent movement and increased load',
      targetMetrics: {
        symmetryIndex: 75,
        velocity: 75,
        strideLength: 110,
        stepTime: 0.7,
        cadence: 85,
        balanceScore: 65,
        weightBearing: 80,
        rangeOfMotion: 70
      },
      exercises: [
        {
          id: 'ex-2-1',
          name: 'Standing Balance Exercises',
          description: 'Standing exercises to improve balance and stability',
          frequency: 'Daily',
          duration: '10 minutes',
          completed: true
        },
        {
          id: 'ex-2-2',
          name: 'Controlled Walking Practice',
          description: 'Slow, deliberate walking focusing on proper form',
          frequency: 'Daily',
          duration: '15 minutes',
          completed: true
        }
      ],
      completed: true,
      startDate: new Date(recoveryStart.getTime() + 30 * 24 * 60 * 60 * 1000), // 30 days after recovery start
      completionDate: new Date(recoveryStart.getTime() + 60 * 24 * 60 * 60 * 1000) // 60 days after recovery start
    },
    {
      id: 'phase-3',
      name: 'Advanced Recovery',
      description: 'Return to normal activities with focus on strength and endurance',
      targetMetrics: {
        symmetryIndex: 90,
        velocity: 100,
        strideLength: 135,
        stepTime: 0.58,
        cadence: 100,
        balanceScore: 85,
        weightBearing: 95,
        rangeOfMotion: 85
      },
      exercises: [
        {
          id: 'ex-3-1',
          name: 'Walking Variations',
          description: 'Varied walking exercises including changes in speed and direction',
          frequency: 'Daily',
          duration: '20 minutes',
          completed: false
        },
        {
          id: 'ex-3-2',
          name: 'Step-Up Exercises',
          description: 'Using stairs or steps to improve strength and stability',
          frequency: 'Every other day',
          duration: '15 minutes',
          completed: true
        }
      ],
      completed: false,
      startDate: new Date(recoveryStart.getTime() + 60 * 24 * 60 * 60 * 1000) // 60 days after recovery start
    }
  ];
  
  // Generate progress snapshots
  const progressSnapshots: ProgressSnapshot[] = [];
  
  // Initial assessment
  progressSnapshots.push({
    id: 'snapshot-1',
    date: new Date(recoveryStart),
    metrics: {
      symmetryIndex: 45,
      velocity: 30,
      strideLength: 70,
      stepTime: 1.1,
      cadence: 55,
      balanceScore: 35,
      weightBearing: 40,
      rangeOfMotion: 35
    },
    patientReported: {
      painLevel: 8,
      confidence: 2,
      difficulty: 9
    },
    notes: 'Initial assessment. Significant pain and limited mobility.',
    phase: 0
  });
  
  // Generate snapshots for phase 1
  for (let i = 1; i <= 4; i++) {
    const snapshotDate = new Date(recoveryStart);
    snapshotDate.setDate(recoveryStart.getDate() + i * 7); // Weekly assessments
    
    const progress = i / 4; // Progress factor from 0.25 to 1
    
    // Calculate metrics based on progress
    const metrics: { [key: string]: number } = {};
    Object.keys(baselineMetrics).forEach(key => {
      const baselineValue = baselineMetrics[key as keyof BaselineMetrics];
      const targetValue = phases[0].targetMetrics[key];
      const initialValue = progressSnapshots[0].metrics[key];
      
      metrics[key] = initialValue + (targetValue - initialValue) * progress;
    });
    
    progressSnapshots.push({
      id: `snapshot-${progressSnapshots.length + 1}`,
      date: snapshotDate,
      metrics,
      patientReported: {
        painLevel: Math.max(2, 8 - i * 1.5),
        confidence: Math.min(9, 2 + i * 1.5),
        difficulty: Math.max(3, 9 - i * 1.5)
      },
      notes: `Week ${i} assessment. ${i === 4 ? 'Phase 1 completion.' : 'Continuing phase 1.'}`,
      phase: 0
    });
  }
  
  // Generate snapshots for phase 2
  for (let i = 1; i <= 4; i++) {
    const snapshotDate = new Date(recoveryStart);
    snapshotDate.setDate(recoveryStart.getDate() + 30 + i * 7); // Weekly assessments in phase 2
    
    const progress = i / 4; // Progress factor from 0.25 to 1
    
    // Calculate metrics based on progress
    const metrics: { [key: string]: number } = {};
    Object.keys(baselineMetrics).forEach(key => {
      const phase1Target = phases[0].targetMetrics[key];
      const phase2Target = phases[1].targetMetrics[key];
      
      metrics[key] = phase1Target + (phase2Target - phase1Target) * progress;
    });
    
    progressSnapshots.push({
      id: `snapshot-${progressSnapshots.length + 1}`,
      date: snapshotDate,
      metrics,
      patientReported: {
        painLevel: Math.max(1, 4 - i * 0.75),
        confidence: Math.min(9, 5 + i),
        difficulty: Math.max(2, 6 - i)
      },
      notes: `Week ${i + 4} assessment. ${i === 4 ? 'Phase 2 completion.' : 'Continuing phase 2.'}`,
      phase: 1
    });
  }
  
  // Generate snapshots for phase 3 (current phase)
  for (let i = 1; i <= 4; i++) {
    const snapshotDate = new Date(recoveryStart);
    snapshotDate.setDate(recoveryStart.getDate() + 60 + i * 7); // Weekly assessments in phase 3
    
    const progress = i / 4; // Progress factor from 0.25 to 1
    
    // Calculate metrics based on progress
    const metrics: { [key: string]: number } = {};
    Object.keys(baselineMetrics).forEach(key => {
      const phase2Target = phases[1].targetMetrics[key];
      const phase3Target = phases[2].targetMetrics[key];
      
      metrics[key] = phase2Target + (phase3Target - phase2Target) * progress;
    });
    
    // Only include snapshots up to today
    if (snapshotDate <= today) {
      progressSnapshots.push({
        id: `snapshot-${progressSnapshots.length + 1}`,
        date: snapshotDate,
        metrics,
        patientReported: {
          painLevel: Math.max(0, 2 - i * 0.5),
          confidence: Math.min(10, 7 + i * 0.5),
          difficulty: Math.max(1, 4 - i * 0.5)
        },
        notes: `Week ${i + 8} assessment. Continuing phase 3.`,
        phase: 2
      });
    }
  }
  
  return {
    id: 'recovery-plan-1',
    patientId: 'patient-123',
    patientName: 'Emma Martinez',
    injuryType: 'Hip Fracture (Right)',
    injuryDate,
    recoveryStart,
    estimatedRecoveryDuration: 120, // 120 days
    phases,
    baselineMetrics,
    currentPhase: 2, // Phase 3 (index 2)
    lastAssessment: progressSnapshots[progressSnapshots.length - 1].date,
    progressSnapshots
  };
};

const mockRecoveryPlan = generateMockRecoveryPlan();

const RecoveryMonitoring: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'progress' | 'baseline' | 'report'>('progress');
  const [recoveryPlan, setRecoveryPlan] = useState<RecoveryPlan>(mockRecoveryPlan);
  const [selectedSnapshotId, setSelectedSnapshotId] = useState<string>(
    recoveryPlan.progressSnapshots[recoveryPlan.progressSnapshots.length - 1].id
  );

  // Calculate days since injury
  const today = new Date();
  const daysSinceInjury = Math.floor(
    (today.getTime() - recoveryPlan.injuryDate.getTime()) / (1000 * 60 * 60 * 24)
  );
  
  // Calculate days since recovery start
  const daysSinceRecoveryStart = Math.floor(
    (today.getTime() - recoveryPlan.recoveryStart.getTime()) / (1000 * 60 * 60 * 24)
  );

  // Calculate recovery progress percentage
  const recoveryProgressPercentage = Math.min(
    100,
    Math.round((daysSinceRecoveryStart / recoveryPlan.estimatedRecoveryDuration) * 100)
  );

  // Helper to format dates
  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleTabChange = (tab: 'progress' | 'baseline' | 'report') => {
    setActiveTab(tab);
  };

  const handleSnapshotChange = (snapshotId: string) => {
    setSelectedSnapshotId(snapshotId);
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold">Recovery Monitoring</h2>
          <p className="text-gray-600">
            Tracking progress for {recoveryPlan.patientName} | {recoveryPlan.injuryType}
          </p>
        </div>
        <div className="flex space-x-2">
          <div className="text-sm font-medium px-3 py-1 bg-blue-100 text-blue-800 rounded-full flex items-center">
            <Calendar className="w-4 h-4 mr-1" />
            Day {daysSinceInjury} since injury
          </div>
          <div className="text-sm font-medium px-3 py-1 bg-green-100 text-green-800 rounded-full flex items-center">
            <Target className="w-4 h-4 mr-1" />
            {recoveryProgressPercentage}% of expected recovery
          </div>
        </div>
      </div>

      {/* Recovery Overview */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <div className="flex justify-between mb-4">
          <div>
            <h3 className="font-medium text-lg">Recovery Timeline</h3>
            <p className="text-gray-600 text-sm">
              Started {formatDate(recoveryPlan.recoveryStart)} |
              Estimated completion: {formatDate(new Date(recoveryPlan.recoveryStart.getTime() + recoveryPlan.estimatedRecoveryDuration * 24 * 60 * 60 * 1000))}
            </p>
          </div>
          <div className="flex items-center">
            <span className="text-sm text-gray-600 mr-2">Currently in:</span>
            <span className="font-medium text-blue-800 px-3 py-1 bg-blue-100 rounded-full text-sm">
              {recoveryPlan.phases[recoveryPlan.currentPhase].name}
            </span>
          </div>
        </div>

        {/* Timeline visualization */}
        <div className="relative pt-5 pb-2">
          <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-blue-500 rounded-full" 
              style={{ width: `${recoveryProgressPercentage}%` }}
            />
          </div>
          <div className="flex justify-between mt-2">
            {recoveryPlan.phases.map((phase, index) => {
              // Calculate position for this phase marker
              const phaseStartPercent = index === 0 
                ? 0 
                : (phase.startDate!.getTime() - recoveryPlan.recoveryStart.getTime()) / 
                  (recoveryPlan.estimatedRecoveryDuration * 24 * 60 * 60 * 1000) * 100;
              
              return (
                <div 
                  key={phase.id}
                  className="relative"
                  style={{ left: `${phaseStartPercent}%`, marginLeft: index === 0 ? '0' : '-12px' }}
                >
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                    index < recoveryPlan.currentPhase 
                      ? 'bg-green-500 text-white' 
                      : index === recoveryPlan.currentPhase 
                      ? 'bg-blue-500 text-white' 
                      : 'bg-gray-300 text-gray-600'
                  }`}>
                    {index + 1}
                  </div>
                  <p className="text-xs mt-1 font-medium text-gray-600" style={{ width: '80px', marginLeft: '-30px', textAlign: 'center' }}>
                    {phase.name}
                  </p>
                  {phase.completionDate && (
                    <p className="text-xs text-gray-500" style={{ width: '80px', marginLeft: '-30px', textAlign: 'center' }}>
                      {formatDate(phase.completionDate)}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="flex overflow-x-auto mb-6 border-b">
        <button
          className={`px-4 py-2 font-medium whitespace-nowrap ${
            activeTab === 'progress'
              ? 'border-b-2 border-blue-500 text-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
          onClick={() => handleTabChange('progress')}
        >
          <TrendingUp className="inline-block mr-2 w-5 h-5" />
          Recovery Progress
        </button>
        <button
          className={`px-4 py-2 font-medium whitespace-nowrap ${
            activeTab === 'baseline'
              ? 'border-b-2 border-blue-500 text-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
          onClick={() => handleTabChange('baseline')}
        >
          <Activity className="inline-block mr-2 w-5 h-5" />
          Baseline Comparison
        </button>
        <button
          className={`px-4 py-2 font-medium whitespace-nowrap ${
            activeTab === 'report'
              ? 'border-b-2 border-blue-500 text-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
          onClick={() => handleTabChange('report')}
        >
          <FileText className="inline-block mr-2 w-5 h-5" />
          Progress Report
        </button>
      </div>

      {/* Main Content */}
      <div className="bg-white rounded-lg shadow">
        {activeTab === 'progress' && (
          <RecoveryProgress 
            recoveryPlan={recoveryPlan}
            selectedSnapshotId={selectedSnapshotId}
            onSnapshotChange={handleSnapshotChange}
          />
        )}
        {activeTab === 'baseline' && (
          <BaselineComparison 
            recoveryPlan={recoveryPlan}
            selectedSnapshotId={selectedSnapshotId}
            onSnapshotChange={handleSnapshotChange}
          />
        )}
        {activeTab === 'report' && (
          <ProgressReport 
            recoveryPlan={recoveryPlan}
          />
        )}
      </div>
    </div>
  );
};

export default RecoveryMonitoring; 